import { Breadcrumb as AntdBreadcrumb } from 'antd';
import React, { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import Icon, { IconTypes } from '../ui/Icon/Icon';
import { theme } from '../ui/themes/default';

type ContextType = [HTMLDivElement | undefined, Dispatch<SetStateAction<HTMLDivElement | undefined>>];

const Context = React.createContext<ContextType>({} as ContextType);

const BreadcrumbsContainer = styled.div`
  margin: 10px 0 10px;
`;

const useBreadcrumbContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error('Missing BreadcrumbProvider.');
  }

  return context;
};

interface BreadcrumbProviderProps {
  children: React.ReactNode;
}
const BreadcrumbProvider: React.FC<BreadcrumbProviderProps> = ({ children }) => {
  const portalNodeState = useState<HTMLDivElement>();

  return <Context.Provider value={portalNodeState}>{children}</Context.Provider>;
};

const BreadcrumbPortal: React.FC = () => {
  const portalNodeRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const [, setPortalNode] = useBreadcrumbContext();

  useEffect(() => {
    setPortalNode(portalNodeRef.current);
  }, [setPortalNode]);

  return (
    <BreadcrumbsContainer className="ant-breadcrumb">
      <ol
        ref={portalNodeRef as any} // eslint-disable-line @typescript-eslint/no-explicit-any
      />
    </BreadcrumbsContainer>
  );
};

interface BreadcrumbProps {
  children: React.ReactNode;
  to?: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ children, to, ...props }) => {
  const [portalNode] = useBreadcrumbContext();

  const renderChildren = () => {
    return to ? <NavLink to={to as string}>{children}</NavLink> : children;
  };

  if (!portalNode) {
    return null;
  }

  return ReactDOM.createPortal(
    <AntdBreadcrumb.Item
      {...props}
      separator={<Icon icon={IconTypes.AngleRight} fillColor={theme.collapseButtonColor} />}
    >
      {renderChildren()}
    </AntdBreadcrumb.Item>,
    portalNode
  );
};

export { Breadcrumb, BreadcrumbPortal, BreadcrumbProvider };
